import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getPagesThin, getPageMetrics } from '../api';
import { getOpportunitySnapshot } from '../apiRankings';
import { getFollowUpsDue, updateTimelineEntry } from '../apiTimeline';
import { useToast } from '../Toast';
import Badge from '../ui/Badge';
import EmptyState from '../ui/EmptyState';
import {
  KIND_FILTERS, indexDomains, fleetValuePerVisit, followUpItems, searchItems, thinItems,
  thinCandidateSites, choreItemsForSources, amazonReportChore, rankItems, filterKind, kindCounts, fmtUsd,
  dropItems, messageItems, weekWindows,
} from './doNext';

const SHOW_FIRST = 10;
const THIN_SITES = 5;
const SNAPSHOT_RETRY_MS = 90 * 1000;
const PARKED_KEY = 'doNext.showParked';

const readPref = () => { try { return localStorage.getItem(PARKED_KEY) === '1'; } catch { return false; } };
const writePref = (on) => { try { localStorage.setItem(PARKED_KEY, on ? '1' : '0'); } catch { /* ignore */ } };

const KIND_BADGE = {
  'follow-up': { state: 'warn', label: 'Follow-up' },
  search: { state: 'info', label: 'Search' },
  thin: { state: 'scheduled', label: 'Thin page' },
  chore: { state: 'draft', label: 'Data chore' },
  drop: { state: 'danger', label: 'Traffic drop' },
  inbox: { state: 'warn', label: 'Message' },
};

const ItemLink = ({ link, primary, onClick }) => {
  const cls = `btn btn-sm ${primary ? 'btn-secondary' : 'btn-ghost'}`;
  if (link.href) {
    return <a className={cls} href={link.href} target="_blank" rel="noopener noreferrer">{link.label} ↗</a>;
  }
  return <Link className={cls} to={link.to} onClick={onClick}>{link.label}</Link>;
};

function Row({ item, onDone, onAmazonImport, onSelectDomain, busy }) {
  const kind = KIND_BADGE[item.kind] || { state: '', label: item.kind };
  const stateBadge = item.state === 'overdue' ? <Badge state="danger">Overdue</Badge>
    : item.state === 'due' ? <Badge state="warn">Due today</Badge> : null;
  const select = () => item.domainId && onSelectDomain && onSelectDomain(item.domainId);
  return (
    <li style={{
      display: 'flex', flexWrap: 'wrap', gap: '8px 16px', alignItems: 'flex-start', justifyContent: 'space-between',
      padding: '10px 0', borderTop: '1px solid var(--color-border)', listStyle: 'none',
    }}>
      <div style={{ flex: '1 1 320px', minWidth: 0 }}>
        <div className="small" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', marginBottom: '2px' }}>
          <Badge state={kind.state}>{kind.label}</Badge>
          {stateBadge}
          <span className="muted" style={{ overflowWrap: 'anywhere' }}>{item.site}</span>
        </div>
        <div style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{item.title}</div>
        <div className="muted small" style={{ overflowWrap: 'anywhere' }}>{item.why}</div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', justifyContent: 'flex-end' }}>
        {item.valueUsd > 0 && (
          <span
            className="small"
            style={{ fontWeight: 600, whiteSpace: 'nowrap' }}
            title={item.estimated
              ? 'Estimated at the fleet’s (or site’s) average value per visit'
              : 'From this site’s own AdSense value per visit'}
          >
            {item.estimated ? '≈' : ''}{fmtUsd(item.valueUsd)}/mo
          </span>
        )}
        {item.action === 'amazon-import' && (
          <button type="button" className="btn btn-primary btn-sm" onClick={onAmazonImport}>Import report</button>
        )}
        <ItemLink link={item.link} primary={item.action !== 'amazon-import'} onClick={select} />
        {(item.links || []).map((l) => <ItemLink key={l.label + (l.to || l.href)} link={l} onClick={select} />)}
        {item.followUp && (
          <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => onDone(item)}>
            {busy ? 'Saving…' : '✓ Mark done'}
          </button>
        )}
      </div>
    </li>
  );
}

/* The console home's ranked "Do next" list. The landing page passes in what
   it already loaded (sites, per-site hits/earnings, freshness, Amazon, the
   inbox alert summary: null while loading, { error } when it failed); this
   card adds follow-ups, the stored search opportunities, and thin pages on
   the top-earning sites. */
export default function DoNextCard({
  domains, metrics, ready, freshness, amazon, messages, onAmazonImport, onSelectDomain,
}) {
  const { addToast } = useToast();
  const [followUps, setFollowUps] = useState({ rows: [], loading: true, failed: false });
  const [opps, setOpps] = useState({ rows: [], loading: true, failed: false, building: false, updatedAt: null });
  const [thin, setThin] = useState({ bySite: {}, pending: 0, checked: 0 });
  const [kind, setKind] = useState('all');
  const [showAll, setShowAll] = useState(false);
  const [showParked, setShowParked] = useState(readPref);
  const [busy, setBusy] = useState({});
  const [done, setDone] = useState(() => new Set());
  const retry = useRef(null);

  const domainsById = useMemo(() => indexDomains(domains), [domains]);

  const loadFollowUps = useCallback(() => {
    getFollowUpsDue(7)
      .then((d) => setFollowUps({ rows: d.follow_ups || [], loading: false, failed: false }))
      .catch(() => setFollowUps({ rows: [], loading: false, failed: true }));
  }, []);

  const loadOpps = useCallback(() => {
    getOpportunitySnapshot(80).then((d) => {
      setOpps({
        rows: d.opportunities || [], loading: false, failed: !!d.error,
        building: !!d.building, updatedAt: d.updated_at || null,
      });
      // first build ever: nothing to show yet, ask again once it has had time
      if (d.building && !(d.opportunities || []).length && !retry.current) {
        retry.current = setTimeout(() => { retry.current = null; loadOpps(); }, SNAPSHOT_RETRY_MS);
      }
    });
  }, []);

  useEffect(() => {
    loadFollowUps();
    loadOpps();
    return () => { if (retry.current) clearTimeout(retry.current); };
  }, [loadFollowUps, loadOpps]);

  /* Thin pages: once hits and earnings are in, check the top earners. */
  const candidates = useMemo(
    () => (ready ? thinCandidateSites(domains, metrics, THIN_SITES) : []),
    [ready, domains, metrics]);
  const candidateKey = candidates.map((d) => d.id).join(',');
  useEffect(() => {
    if (!candidateKey) return undefined;
    let cancelled = false;
    const ids = candidateKey.split(',');
    setThin({ bySite: {}, pending: ids.length, checked: 0 });
    ids.forEach((id) => {
      Promise.all([getPagesThin(id).catch(() => null), getPageMetrics(id)])
        .then(([t, m]) => {
          if (cancelled) return;
          setThin((s) => ({
            bySite: { ...s.bySite, [id]: { thin: t, metrics: m } },
            pending: s.pending - 1,
            checked: s.checked + (t ? 1 : 0),
          }));
        });
    });
    return () => { cancelled = true; };
  }, [candidateKey]);

  const items = useMemo(() => {
    const fleetVpv = ready ? fleetValuePerVisit(domains, metrics) : 0;
    const out = [
      ...followUpItems(followUps.rows, domainsById),
      ...messageItems(messages, domainsById),
      ...choreItemsForSources(freshness ? freshness.problems : []),
    ];
    const amazonChore = amazonReportChore(amazon);
    if (amazonChore) out.push(amazonChore);
    if (ready) {
      out.push(...searchItems(opps.rows, domainsById, {
        fleetVpv, hideParked: !showParked, earningsOf: metrics.earnings,
      }));
      out.push(...dropItems(domains, metrics, weekWindows(), { fleetVpv, hideParked: !showParked }));
      Object.entries(thin.bySite).forEach(([id, r]) => {
        const d = domainsById[id];
        const rpm = d && metrics.rpm ? metrics.rpm(d) : null;
        out.push(...thinItems(id, d, r.thin, r.metrics, { siteRpm: rpm || 0, fleetVpv }));
      });
    }
    return rankItems(out.filter((i) => !done.has(i.id)));
  }, [followUps.rows, messages, domainsById, freshness, amazon, ready, opps.rows, showParked, metrics, domains,
    thin.bySite, done]);

  const counts = kindCounts(items);
  const shown = filterKind(items, kind);
  const visible = showAll ? shown : shown.slice(0, SHOW_FIRST);

  const markDone = async (item) => {
    const { domainId, id } = item.followUp;
    setBusy((b) => ({ ...b, [item.id]: true }));
    try {
      await updateTimelineEntry(domainId, id, { status: 'done' });
      setDone((s) => new Set(s).add(item.id));
      addToast('Follow-up marked done', 'success');
    } catch (err) {
      addToast(`Could not mark it done: ${err.response?.data?.detail || err.message}`, 'error');
    } finally {
      setBusy((b) => ({ ...b, [item.id]: false }));
    }
  };

  const toggleParked = () => setShowParked((v) => { writePref(!v); return !v; });

  const selectDomain = (domainId) => {
    const d = domainsById[domainId];
    if (d && onSelectDomain) onSelectDomain(d);
  };

  const loadingParts = [
    followUps.loading && 'follow-ups',
    messages == null && 'messages',
    opps.loading && 'search opportunities',
    !ready && 'traffic and earnings',
    ready && thin.pending > 0 && `thin pages (${candidates.length - thin.pending} of ${candidates.length} sites)`,
  ].filter(Boolean);
  const notes = [
    followUps.failed && 'Follow-ups could not be loaded.',
    messages && messages.error && 'Reader feedback and contact messages could not be loaded.',
    opps.failed && 'Search opportunities could not be loaded.',
    !opps.failed && opps.building && !opps.rows.length
      && 'Search opportunities are being worked out for the first time; they appear here in a few minutes.',
  ].filter(Boolean);

  return (
    <section className="card" style={{ marginBottom: '20px' }} aria-label="Do next" id="do-next">
      <div className="card-header" style={{ flexWrap: 'wrap', gap: '8px' }}>
        <div className="muted small" style={{ flex: '1 1 280px' }}>
          What to work on to grow earnings, most urgent first: overdue follow-ups and waiting
          messages, data chores, then search, thin-page and traffic-drop fixes by the money at stake.
        </div>
        <div className="chip-group" role="group" aria-label="Filter the list">
          {KIND_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`chip${kind === f.key ? ' active' : ''}`}
              aria-pressed={kind === f.key}
              onClick={() => setKind(f.key)}
            >
              {f.label}{counts[f.key] ? <span className="count"> {counts[f.key]}</span> : null}
            </button>
          ))}
          <button
            type="button"
            className={`chip${showParked ? ' active' : ''}`}
            aria-pressed={showParked}
            onClick={toggleParked}
            title="Parked sites have at most 2 pages and no AdSense earnings"
          >
            Show parked sites
          </button>
        </div>
      </div>

      {notes.map((n) => <div key={n} className="muted small" style={{ marginBottom: '6px' }}>{n}</div>)}

      {visible.length === 0 ? (
        loadingParts.length ? (
          <div className="muted small" style={{ padding: '12px 0' }}>Loading {loadingParts.join(', ')}…</div>
        ) : (
          <EmptyState
            title="Nothing to do"
            message={kind === 'all'
              ? 'No follow-ups or messages waiting, no stale data, and no search, thin-page or traffic-drop fixes worth making right now.'
              : 'Nothing of this kind right now.'}
          />
        )
      ) : (
        <ol style={{ margin: 0, padding: 0 }}>
          {visible.map((item) => (
            <Row
              key={item.id}
              item={item}
              busy={!!busy[item.id]}
              onDone={markDone}
              onAmazonImport={onAmazonImport}
              onSelectDomain={selectDomain}
            />
          ))}
        </ol>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid var(--color-border)', paddingTop: '8px' }}>
        <span className="muted small">
          {loadingParts.length > 0 && visible.length > 0 ? `Still loading ${loadingParts.join(', ')}… ` : ''}
          Dollar figures are estimates a month: search upside at the top 3, thin pages by what they earn now,
          traffic drops by the views lost against a typical week.
          {ready && candidates.length > 0 && ` Thin pages checked on the ${candidates.length} top-earning sites.`}
        </span>
        {shown.length > SHOW_FIRST && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'Show fewer' : `Show all ${shown.length}`}
          </button>
        )}
      </div>
    </section>
  );
}
